import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useApi } from '../../api/useApi';
import { useReferences } from '../../helpers/context_manager/useReferences';
import { toOptions, Option } from '../../helpers/toOptions';

export type Report = {
  name: string;
  url: string;
  columns: ReportColumn[];
  filters: ReportFilterItem[];
  onRowClick?: (row: ReportDataRow) => void;
};

export type ReportColumn = {
  id?: string;
  label: string;
  key: string;
  hidden?: boolean;
  type?: 'text' | 'date' | 'number' | 'select' | 'boolean' | null;
  defaultValue?: string | number | null;
  sortableBy?: string | null;
  options?: Option[];
};

export type ReportDataRow = {
  [key: string]: string | number | null;
};

export type ReportTableData = {
  columns: ReportColumn[];
  rows: ReportDataRow[];
  total: number;
};

export type ReportFilterItem = {
  label: string;
  key: string;
  type: 'select' | 'date' | 'text';
  options?: Option[];
  defaultValue?: string | number | null;
  required?: boolean;
};

export type ReportTableParams = {
  limit?: number;
  offset?: number;
  ordering?: string | null;
};

export type ReportTableFilter = {
  [key: string]: string | number | null;
};

export const useStatistics = () => {
  const api = useApi();
  const navigate = useNavigate();

  const { references, loading: referencesLoading } = useReferences();
  const [ loading, setLoading ] = useState(false);  

  const defaultFilters: ReportFilterItem[] = [
    { label: 'Období od', key: 'autopsy_date_after', type: 'date' },
    { label: 'Období do', key: 'autopsy_date_before', type: 'date' },
    { label: 'Pitvající lékař', key: 'doctor_joint_id', type: 'select', options: toOptions(references.employees) }
  ];

  const reports: Report[] = [
    {
      name: 'Prolomení mlčenlivosti (OVM)',
      url: '/v1/autopsy-protocols/statistics?confidentiality_waiver=true',
      columns: [
        { label: 'ID', key: 'id', hidden: true },
        { label: 'Pitevní protokol', key: 'protocol_display' },
        { label: 'Zemřelý', key: 'deceased_name'},
        { label: 'Místo úmrtí', key: 'place_of_death' },
        { label: 'Datum pitvy', key: 'autopsy_date', type: 'date', sortableBy: 'autopsy_date' },
      ],
      filters: [...defaultFilters],
      onRowClick: (row) => {
        navigate(`/protocol/${row.id}/protocol/`);
      }
    },
    {
      name: 'Přerušení pitvy',
      url: '/v1/autopsy-protocols/statistics?is_interrupted=true',
      columns: [
        { label: 'ID', key: 'id', hidden: true },
        { label: 'Pitevní protokol', key: 'protocol_display' },
        { label: 'Zemřelý', key: 'deceased_name'},
        { label: 'Místo úmrtí', key: 'place_of_death' },
        { label: 'Datum pitvy', key: 'autopsy_date', type: 'date', sortableBy: 'autopsy_date' },
      ],
      filters: [...defaultFilters],
      onRowClick: (row) => {
        navigate(`/protocol/${row.id}/protocol/`);
      }
    },
    {
      name: 'Hlášení SÚKL nežádoucí účinky',
      url: '/v1/autopsy-protocols/statistics?sent_side_effects_report=true',
      columns: [
        { label: 'ID', key: 'id', hidden: true },
        { label: 'Pitevní protokol', key: 'protocol_display' },
        { label: 'Zemřelý', key: 'deceased_name'},
        { label: 'Místo úmrtí', key: 'place_of_death' },
        { label: 'Datum pitvy', key: 'autopsy_date', type: 'date', sortableBy: 'autopsy_date' },
      ],
      filters: [...defaultFilters],
      onRowClick: (row) => {
        navigate(`/protocol/${row.id}/protocol/`);
      }
    },
    {
      name: 'Hlášení NOR',
      url: '/v1/autopsy-protocols/statistics?sent_to_NOR=true',
      columns: [
        { label: 'ID', key: 'id', hidden: true },
        { label: 'Pitevní protokol', key: 'protocol_display' },
        { label: 'Zemřelý', key: 'deceased_name'},
        { label: 'Místo úmrtí', key: 'place_of_death' },
        { label: 'Datum pitvy', key: 'autopsy_date', type: 'date', sortableBy: 'autopsy_date' },
      ],
      filters: [...defaultFilters],
      onRowClick: (row) => {
        navigate(`/protocol/${row.id}/protocol/`);
      }
    },
    {
      name: 'Hlášení TBC',
      url: '/v1/autopsy-protocols/statistics?sent_to_TBC=true',
      columns: [
        { label: 'ID', key: 'id', hidden: true },
        { label: 'Pitevní protokol', key: 'protocol_display' },
        { label: 'Zemřelý', key: 'deceased_name'},
        { label: 'Místo úmrtí', key: 'place_of_death' },
        { label: 'Datum pitvy', key: 'autopsy_date', type: 'date', sortableBy: 'autopsy_date' },
      ],
      filters: [...defaultFilters],
      onRowClick: (row) => {
        navigate(`/protocol/${row.id}/protocol/`);
      }
    },
    {
      name: 'Jmenný seznam zemřelých',
      url: '/v1/deceased/',
      columns: [
        { label: 'protocol_id', key: 'latest_autopsy_protocol_id', hidden: true },
        { label: 'Zemřelý', key: 'full_name_with_titles', defaultValue: 'Neznámý' },
        { label: 'Rodné číslo', key: 'birth_id' },
        { label: 'Pohlaví', key: 'gender_display' },
        { label: 'Stáří', key: 'age_years', type: 'number' },
        { label: 'Datum narození', key: 'date_of_birth', type: 'date' },
        { label: 'Datum úmrtí', key: 'date_of_death', type: 'date' },
        { label: 'Pojišťovna', key: 'insurance_company', type: 'select', options: toOptions(references.insurances) },
        { label: 'Poznámka', key: 'note' }
      ],
      filters: [...defaultFilters,
        { label: 'Pojišťovna', key: 'insurer_code', type: 'select', options: toOptions(references.insurances, 'name', (item) => `${item.code} - ${item.name}`, undefined, (item) => item.code) }
      ],
      onRowClick: (row) => {
        navigate(`/protocol/${row.latest_autopsy_protocol_id}/patient/`);
      }
    }
  ];

  const loadReportTableData = async (report: Report, filter: ReportTableFilter, params: ReportTableParams): Promise<ReportTableData> => {
    setLoading(true);

    const urlParams = new URLSearchParams({
      ...Object.fromEntries(
        Object.entries(filter)
          .filter(([_, v]) => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0))
          .map(([k, v]: [string, any]) => [k, typeof v === "object" && v !== null ? v.value : v])
        ),
      ...Object.fromEntries(
          Object.entries(params)
          .filter(([_, v]) => v !== undefined && v !== null && v !== "")
      )
    }).toString();

    const paramsChar = report.url.includes('?') ? '&' : '?';
    const res = await api.statistics.get(`${report.url}${paramsChar}${urlParams}`);
    const rows = await res.data.results;
    setLoading(false);

    return { columns: report.columns, rows, total: res.data.count };
  };

  return {
    loading: loading || referencesLoading,
    reports: reports,
    loadReportTableData
  };
};
